import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const PILLAR_ONE_OVERVIEW = String.raw`
Pillar 1 measures how well the user connects news to market instruments and
turns that view into conviction sliders.


### Terms used

- **ICS — Individual Conviction Score:** one news item's expected impact on one
  instrument. Its positive or negative sign gives the direction, and its
  distance from zero gives the strength.
- **PCS — Period Conviction Score:** one instrument's signed ICS values added
  together across all news in the period.
- **Adapted PCS:** PCS converted to the same $-5$ to $+5$ range as the user's
  slider.
- **Agreement:** a value from $0$ to $1$ showing whether the ICS impacts point
  the same way. Values near $1$ mean they align; values near $0$ mean positive
  and negative impacts conflict.

### The four components

- **News mapping — 40 points:** The user links each news item to the instruments
  it affects. A link is expected when its answer-key ICS is at least $0.25$
  away from zero. Correct links raise the score; missed expected links and
  extra links lower it.
- **Slider direction — 25 points:** When adapted PCS is at least $0.35$ away
  from zero, PM compares its positive or negative direction with the user's
  last saved slider. The same direction gets full credit, zero gets $45\%$,
  and the opposite direction gets no credit. Stronger adapted PCS values count
  more.
- **Slider magnitude — 25 points:** PM measures how far the user's last saved
  slider is from adapted PCS, including whether it is positive or negative.
  Each slider step of distance removes $20\%$ of the instrument's credit;
  five or more steps gets no credit. Stronger adapted PCS values count more.
- **Restraint when unclear — 10 points:** A view is unclear when adapted PCS is
  less than $0.35$ from zero or Agreement is below $0.35$. A slider at zero gets
  full credit, and each step away removes $25\%$. Qualifying instruments count
  equally. If none qualify, this component gets full credit.

Each component is first calculated as a percentage from $0$ to $100$. PM then
applies its weight to form the period score:

$$
P_p=0.40\,\mathrm{Mapping}_p
  +0.25\,\mathrm{Direction}_p
  +0.25\,\mathrm{Magnitude}_p
  +0.10\,\mathrm{Restraint}_p.
$$

The final Pillar 1 score is the simple average of all period scores, so every
period has equal weight. Instrument returns and profit or loss may be shown for
context, but they do not change Pillar 1 points.
`;

const PillarOneOverviewGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-one-overview-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">100 points</Tag>
      </div>
      <Typography.Title id="pillar-one-overview-title" level={4}>
        Pillar 1 overview
      </Typography.Title>
      <Typography.Text type="secondary">
        How news interpretation and conviction become one score.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {PILLAR_ONE_OVERVIEW}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarOneOverviewGuide;
